import React, { useState } from 'react';
import Button from '@mui/material/Button';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import { CustomMuiIcon } from 'assets/images/icons/customMuiIcons';
import CardMenu from 'components/Menu/CardMenu/CardMenu';
import {
  TCardType,
  ECardTypes,
  TMenuOption,
  TUserRole,
  EUserRole,
} from 'shared/types/card.interface';
import { TChallengeStatus, EChallengeStatus } from 'shared/types/challenges.interface';
import { TDebateStatus, EDebateStatuses } from 'shared/types/debates.interface';
import { useCancelChallengeMutation } from 'redux/api/challenge.api';
import { useCancelDebateMutation } from 'redux/api/debate.api';
import { useLoading } from 'shared/hooks/useLoading';
import { DEBATES_FETCHES_SUCCESS } from 'shared/constants/alert-constants';
import {
  CHALLENGE_CARD_MENU_INITIATOR,
  DEBATE_CARD_MENU_INITIATOR,
  DEBATE_CARD_MENU_OPPONENT,
} from 'shared/constants/menu-constants';
import {
  DIALOG_INITIATOR_CANCEL_DEBATE,
  DIALOG_OPPONENT_CANCEL_DEBATE,
  DIALOG_CANCEL_CHALLENGE,
} from 'shared/constants/modal-constants';

interface ICardMenuOpenButtonProps {
  type: TCardType;
  userRole: TUserRole;
  status: TChallengeStatus | TDebateStatus;
  id: number;
}

type TMenuOptions = {
  [M in TCardType]: {
    [K in TUserRole]: TMenuOption[];
  };
};

export const CardMenuOpenButton: React.FunctionComponent<ICardMenuOpenButtonProps> = ({
  type,
  userRole,
  status,
  id,
}) => {
  const [isCardMenuOpen, setCardMenuOpen] = useState<boolean>(false);
  const [cancelChallenge, { isLoading: isLoadingCancelChallenge }] = useCancelChallengeMutation();
  const [cancelDebate, { isLoading: isLoadingCancelDebate }] = useCancelDebateMutation();
  useLoading(isLoadingCancelChallenge || isLoadingCancelDebate);

  const handleCancelEvent = (message: string) => {
    cancelDebate({ id, message });
  };

  const menuOptions: TMenuOptions = {
    [ECardTypes.Challenge]: {
      [EUserRole.Initiator]: [
        status === EChallengeStatus.Waiting && {
          name: CHALLENGE_CARD_MENU_INITIATOR.CancelChallenge,
          dialogInfo: {
            title: DIALOG_CANCEL_CHALLENGE.title,
            text: DIALOG_CANCEL_CHALLENGE.text,
            сonsentText: DIALOG_CANCEL_CHALLENGE.сonsentText,
            сonsentCallback: () => cancelChallenge(id),
            dissentText: DIALOG_CANCEL_CHALLENGE.dissentText,
          },
        },
      ].filter(Boolean),
      [EUserRole.Opponent]: [].filter(Boolean),
      [EUserRole.RegularUser]: [].filter(Boolean),
    },
    [ECardTypes.Debate]: {
      [EUserRole.Initiator]: [
        status === EDebateStatuses.Scheduled && {
          name: DEBATE_CARD_MENU_INITIATOR.CancelDebate,
          dialogInfo: {
            title: DIALOG_INITIATOR_CANCEL_DEBATE.title,
            text: DIALOG_INITIATOR_CANCEL_DEBATE.text,
            сonsentText: DIALOG_INITIATOR_CANCEL_DEBATE.сonsentText,
            сonsentCallback: () => handleCancelEvent(DEBATES_FETCHES_SUCCESS.cancelDebateInitiator),
            dissentText: DIALOG_INITIATOR_CANCEL_DEBATE.dissentText,
          },
        },
      ].filter(Boolean),
      [EUserRole.Opponent]: [
        status === EDebateStatuses.Scheduled && {
          name: DEBATE_CARD_MENU_OPPONENT.CancelDebate,
          dialogInfo: {
            title: DIALOG_OPPONENT_CANCEL_DEBATE.title,
            text: DIALOG_OPPONENT_CANCEL_DEBATE.text,
            сonsentText: DIALOG_OPPONENT_CANCEL_DEBATE.сonsentText,
            сonsentCallback: () => handleCancelEvent(DEBATES_FETCHES_SUCCESS.cancelDebateOpponent),
            dissentText: DIALOG_OPPONENT_CANCEL_DEBATE.dissentText,
          },
        },
      ].filter(Boolean),
      [EUserRole.RegularUser]: [].filter(Boolean),
    },
  };

  if (menuOptions[type][userRole].length === 0) return null;

  return (
    <ClickAwayListener onClickAway={() => setCardMenuOpen(false)}>
      <Button
        variant="link"
        onClick={() => setCardMenuOpen(!isCardMenuOpen)}
        sx={(theme) => ({
          display: 'flex',
          minWidth: { lg: '24px', md: '26px', sm: '26px', xs: '26px' },
          alignSelf: 'stretch',
          borderRadius: '4px',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: isCardMenuOpen ? theme.palette.myGrey.grey100 : 'transparent',
          '&:hover': {
            backgroundColor: theme.palette.myGrey.grey100,
            opacity: 1,
          },
        })}
        disableRipple
      >
        <CustomMuiIcon type="icon-button-more" size="16px" />
        <CardMenu cardMenuIsOpen={isCardMenuOpen} menuOptions={menuOptions[type][userRole]} />
      </Button>
    </ClickAwayListener>
  );
};
